import React, { Children, useEffect, useMemo, useState } from "react";
import Table from "../../components/utils/Table";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import Button from "../../components/utils/Button";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import CardLayout from "../../components/utils/CardLayout";
import ToastAlert from "../../components/utils/ToastAlert";
import dealerActions from "../../redux/dealer/actions";
import Modal from '../../components/utils/Modal'

const { createDealer, getDealerList, resetMessage, modifyDealer, } = dealerActions;

const Dealer = () => {
  const dispatch = useDispatch();
  const [searchItem, setSearchItem] = useState("");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [showEmail, setShowEmail] = useState(true);
  const [emailInput, setEmailInput] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const { dealerList, getDealerError, getDealerMessage, getDealerListLoading, modifyDealerLoading, modifyDealerError, modifyDealerMessage, } = useSelector((state) => state.dealer || {});
  const { register, handleSubmit, reset, watch, setValue, clearErrors, formState: { errors }, } = useForm({
    mode: "onChange", defaultValues: { dealerId: "", userId: "", password: "", email: "", isActive: true, },
  });
  // console.log("dealerList==>>", dealerList)

  const generateDealerId = () => {
    const now = new Date();
    const year = String(now.getFullYear()).slice(-2);
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hour = String(now.getHours()).padStart(2, "0");
    const minute = String(now.getMinutes()).padStart(2, "0");
    const second = String(now.getSeconds()).padStart(2, "0");
    const millisecond = String(now.getMilliseconds()).padStart(3, "0");
    return `DL${year}${month}${day}${hour}${minute}${second}${millisecond}`;
  }
  const generateUserId = () => {
    const prefix = "USR";
    const digits = new Uint8Array(12);
    crypto.getRandomValues(digits);
    const randomDigits = Array.from(
      digits,
      (value) => value % 10
    ).join("");
    return `${prefix}${randomDigits}`;
  };
  const regenerateDealerId = async () => {
    const newDealerId = generateDealerId();
    setValue("dealerId", newDealerId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  const regenerateUserId = async () => {
    const newUserId = generateUserId();
    setValue("userId", newUserId, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    dispatch(getDealerList());
  }, [dispatch]);

  useEffect(() => {
    if (getDealerError) {
      ToastAlert({ type: "error", text: getDealerError, });
    }
    if (getDealerMessage) {
      ToastAlert({ type: "success", text: getDealerMessage, });
      closeModal();
      dispatch(resetMessage());
    }
  }, [getDealerError, getDealerMessage, dispatch]);

  // Modify Dealer Success / Error
  // --------------------------------------------------
  useEffect(() => {
    if (modifyDealerError) {
      ToastAlert({
        type: "error",
        text: modifyDealerError,
      });
      dispatch(resetMessage());
      return;
    }

    if (modifyDealerMessage) {
      ToastAlert({
        type: "success",
        text: modifyDealerMessage,
      });
      setConfirmAction(null);
      dispatch(getDealerList());
      dispatch(resetMessage());
    }
  }, [
    modifyDealerError,
    modifyDealerMessage,
    dispatch,
  ]);


  const filteredItems = useMemo(() => {
    return Array.isArray(dealerList)
      ? dealerList.filter((item) => {
        if (!searchItem) return true;

        return Object.values(item).some(
          (value) =>
            typeof value === "string" &&
            value.toLowerCase().includes(searchItem.toLowerCase())
        );
      })
      : [];
  }, [dealerList, searchItem]);
  // Add Dealer Submit
  // --------------------------------------------------
  const handleAddDealerSubmit = (data) => {
    const payload = {
      dealerId: data.dealerId,
      userId: data.userId,
      password: data.password,
      emailId: data.email,
      isActive: data.isActive,
    };

    console.log(
      "CREATE DEALER PAYLOAD =>",
      payload
    );

    dispatch(createDealer(payload));
  };

  useEffect(() => {
    if (!confirmAction) return;

    const handleOutsideClick = (event) => {
      const popup = document.querySelector(".dealer-confirm-popup");

      if (popup && !popup.contains(event.target)) {
        setConfirmAction(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [confirmAction]);

  const handleStatusChange = (item) => {
    const newStatus = !item?.IsActive;
    setConfirmAction({
      distributorId: item?.DistributorID,
      isActive: newStatus,
    });
  };

  const handleConfirmStatusChange = () => {
    if (!confirmAction?.distributorId) {
      ToastAlert({
        type: "error",
        text: "Dealer ID is not available.",
      });
      return;
    }
    const payload = {
      distributorID: confirmAction.distributorId,
      isActive: confirmAction.isActive,
    };

    console.log("MODIFY DEALER PAYLOAD =>", payload);

    dispatch(modifyDealer(payload));
  };

  const dataSource = useMemo(() => {
    return filteredItems.map((item, index) => ({
      key: index + 1,
      distributorId: item?.DistributorID ?? "-",
      dealerId: item?.DealerID ?? "-",
      parentACode: item?.ParentACode ?? "-",
      status: (<span style={{ textAlign: "center" }} className={`badge ${item?.IsActive ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}`}>
        {item?.IsActive ? "Active" : "Inactive"}
      </span>),
      createdDate: item?.CreatedDtTm
        ? new Date(item.CreatedDtTm).toLocaleString()
        : "-",
      lastModifiedDate: item?.LastModifiedDtTm
        ? new Date(item.LastModifiedDtTm).toLocaleString()
        : "-",
      action: (
        <div
          className="position-relative d-flex gap-2"
          style={{ width: "fit-content" }}
        >
          <button
            type="button"
            className={`btn btn-sm ${item?.IsActive
                ? "btn-outline-danger"
                : "btn-outline-success"
              }`}
            title={item?.IsActive ? "Deactivate" : "Activate"}
            onClick={() => handleStatusChange(item)}
            disabled={modifyDealerLoading}
          >
            <i
              className={
                item?.IsActive
                  ? "ri-close-circle-line"
                  : "ri-checkbox-circle-line"
              }
            />
          </button>

          {confirmAction?.distributorId === item?.DistributorID && (
            <div
              className="dealer-confirm-popup position-absolute bg-white border rounded shadow-sm p-3"
              style={{
                zIndex: 1050,
                minWidth: "220px",
                right: "100%",
                top: "50%",
                transform: "translateY(-50%)",
                marginRight: "8px",
              }}
            >
              <h6 className="mb-2">
                Are you sure you want{" "}
                <strong>
                  {confirmAction.isActive ? "activate" : "deactivate"}
                </strong>{" "}
                this dealer?
              </h6>

              <div className="d-flex justify-content-start gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={handleConfirmStatusChange}
                  disabled={modifyDealerLoading}
                >
                  {modifyDealerLoading ? "..." : "Yes"}
                </button>

                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={() => setConfirmAction(null)}
                  disabled={modifyDealerLoading}
                >
                  No
                </button>
              </div>
            </div>
          )}
        </div>
      ),
    }));
  }, [filteredItems, modifyDealerLoading, confirmAction]);

  const columns = [
    { displayName: "Sr.No.", key: "key", },
    { displayName: "Dealer", key: "dealerId", },
    { displayName: "Created By", key: "parentACode", },
    { displayName: "Created Date", key: "createdDate", },
    { displayName: "Modified Date", key: "lastModifiedDate", },
    { displayName: "Status", key: "status", },
    { displayName: "Action", key: "action", },
  ];

  const maskEmail = (email) => {
    if (!email) return "";
    const atIndex = email.indexOf("@");
    if (atIndex === -1) {
      if (email.length <= 1) return email;
      return email.charAt(0) + "*".repeat(email.length - 1);
    }

    const username = email.substring(0, atIndex);
    const domain = email.substring(atIndex);

    if (username.length <= 1) {
      return username + domain;
    }

    return (
      username.charAt(0) +
      "*".repeat(username.length - 1) +
      domain
    );
  };

  const downloadResponseFile = () => {
    const data = dataSource.map((item, key) => ({
      "Sr.No.": key + 1,
      "Dealer ID": item?.dealerId,
      "Parent ACode": item?.parentACode,
      "Status": item?.IsActive ? "Active" : "Inactive",
      "Created Date": item?.createdDate,
      "Last Modified Date": item?.lastModifiedDate,
    }));

    downloadExcelSampleFile(data, "dealer-list.xlsx");
  };

  const openDealerModal = () => {
    const dealerId = generateDealerId();
    const userId = generateUserId();

    reset({
      dealerId,
      userId,
      password: "",
      confirmPassword: "",
      email: "",
      isActive: true,
    });
    setEmailInput("");
    setShowEmail(true);
    setShowPassword(false);
    setIsOpenModal(true);
  }

  const closeModal = () => {
    setIsOpenModal(false);
    reset({
      dealerId: "",
      userId: "",
      password: "",
      email: "",
      isActive: true,
    });
    clearErrors()
    setEmailInput("");
    setShowEmail(true);
    setShowPassword(false);
  }


  return (
    <CardLayout title="Dealers" icon="fas fa-user-tie"
      rightContent={
        <div className="d-flex align-items-center gap-2">

          {/* Search */}
          <div style={{ width: "300px" }}>
            <input
              type="search"
              className="form-control"
              placeholder="Search..."
              value={searchItem}
              onChange={(e) => setSearchItem(e.target.value)}
            />
          </div>

          {/* Excel */}
          <Button
            title="Excel"
            buttonType="button"
            variant="secondary"
            icon={
              <i className="ri-download-cloud-line me-2"></i>
            }
            onClick={downloadResponseFile}
          />

          {/* Add Dealer */}
          <Button
            title="Add Dealer"
            buttonType="button"
            variant="primary"
            icon={
              <i className="ri-add-line me-1"></i>
            }
            onClick={openDealerModal}
          />

        </div>
      }>
      {/* Table */}
      <div>
        <Table
          columns={columns}
          loading={getDealerListLoading}
          data={dataSource}
          pagination={true}
          tableHeight="380px"
        />
      </div>
      <Modal
        showModal={isOpenModal}
        title="Add Dealer"
        onHide={closeModal}
        width={700}
        showFooter={false}
      >
        <form className="row" onSubmit={handleSubmit(handleAddDealerSubmit)}>
          {/* Dealer ID */}
          <div className="form-group col-md-6 mb-3">
            <label className="form-label fw-semibold">
              Dealer ID{" "}
              <small style={{ fontSize: "10px" }}>(Auto-Generated)</small>
              <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <input
                type="text"
                className="form-control bg-light"
                {...register("dealerId", {
                  required: "Dealer ID is required",
                })}
                readOnly
                style={{ backgroundColor: "var(--bg-content)" }}
              />
              <span
                className="input-group-text bg-white"
                onClick={regenerateDealerId}
                title="Generate User ID"
                style={{ cursor: "pointer" }}
              >
                <i className="ri-refresh-line"></i>
              </span>
            </div>
            {errors.dealerId && (
              <div className="text-danger small">
                {
                  errors.dealerId.message
                }
              </div>
            )}
          </div>
          {/* User ID */}
          <div className="form-group col-md-6 mb-3">
            <label className="form-label fw-semibold">
              User ID{" "}
              <small style={{ fontSize: "10px" }}>(Auto-Generated)</small>
              <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <input
                type="text"
                className="form-control bg-light"
                {...register("userId", {
                  required: "User ID is required",
                })}
                readOnly
              />
              <span
                className="input-group-text bg-white"
                onClick={regenerateUserId}
                title="Generate User ID"
                style={{ cursor: "pointer" }}
              >
                <i className="ri-refresh-line"></i>
              </span>
            </div>

            {errors.userId && (
              <div className="text-danger small">
                {errors.userId.message}
              </div>
            )}
          </div>
          {/* Email Address */}
          <div className="form-group col-md-6 mb-3">
            <label className="form-label fw-semibold">
              Email Address{" "}
              <small style={{ fontSize: "10px" }}>
                (Used only for password reset)
              </small>
              <span className="text-danger">*</span>
            </label>

            <div className="input-group">
              <input
                type="text"
                placeholder="Enter email address"
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                value={showEmail ? emailInput : maskEmail(emailInput)}
                {...register("email", {
                  required: "Email Address is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email address",
                  },
                  onChange: (e) => {
                    const value = e.target.value.toLowerCase();
                    setEmailInput(value);
                  },
                })}
              />

              <button
                type="button"
                className="input-group-text"
                onClick={() => {
                  setShowEmail((prev) => !prev);
                }}
                style={{
                  cursor: "pointer",
                  backgroundColor: "var(--bg-content)",
                  borderColor: "var(--border-color)",
                  color: "var(--hedging-text-color)",
                  minWidth: "42px",
                  justifyContent: "center",
                }}
                title={showEmail ? "Hide Email" : "Show Email"}
              >
                <i
                  className={
                    showEmail
                      ? "ri-eye-off-line"
                      : "ri-eye-line"
                  }
                  style={{ fontSize: "18px" }}
                />
              </button>
            </div>

            {errors.email && (
              <div className="invalid-feedback d-block">
                {errors.email.message}
              </div>
            )}
          </div>
          {/* Password */}
          <div className="form-group col-md-6 mb-3">
            <label htmlFor="password" className="form-label fw-semibold">
              Password <span className="text-danger">*</span>
            </label>

            <div className="input-group">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                className={`form-control ${errors.password ? "is-invalid" : ""
                  }`}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                  pattern: {
                    value:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    message:
                      "Use uppercase, lowercase, number & special character.",
                  },
                })}
              />

              <span
                className="input-group-text"
                onClick={() => setShowPassword((prev) => !prev)}
                style={{ cursor: "pointer" }}
              >
                <i
                  className={
                    showPassword
                      ? "ri-eye-off-line"
                      : "ri-eye-line"
                  }
                />
              </span>
            </div>

            {errors.password && (
              <div className="invalid-feedback d-block">
                {errors.password.message}
              </div>
            )}
          </div>
          {/* Status */}
          <div className="form-group col-md-6">
            <label className="form-label fw-semibold">
              Status
            </label>

            <div className="d-flex align-items-center gap-2 ">
              <div className="form-check form-switch m-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="isActiveSwitch"
                  {...register("isActive")}
                />
              </div>

              <span className="fw-semibold">
                {watch("isActive") ? "Active" : "Inactive"}
              </span>
            </div>
          </div>

          {/* Buttons */}
          <div className="col-md-12 d-flex justify-content-end gap-3">
            <Button
              title="Close"
              buttonType="button"
              variant="secondary"
              onClick={closeModal}
              icon={<i className="ri-close-circle-line me-1"></i>}
            />

            <Button
              title="Submit "
              buttonType="submit"
              variant="primary"
              icon={<i className="ri-send-plane-fill me-1"></i>}
            />
          </div>

        </form>
      </Modal>

    </CardLayout>
  );
};
export default Dealer;
